<script lang="ts">

  import { goto } from "$app/navigation"
  import { page } from "$app/state"
  import httpClient from "$lib/axios-client"
  import { Button } from "$lib/components/ui/button"
  import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
  } from "$lib/components/ui/command"
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
  } from "$lib/components/ui/dropdown-menu"
  import { Input } from "$lib/components/ui/input"
  import { Label } from "$lib/components/ui/label"
  import { Popover, PopoverButton, PopoverContent, PopoverTrigger } from "$lib/components/ui/popover"
  import { RelationEmpty } from "$lib/data/schema/_relation"
  import type { Relation } from "$lib/data/type/_relation"
  import type { _response } from "$lib/data/type/_response"
  import { createFilter } from "$lib/hooks/filter.svelte"
  import { createSearch } from "$lib/hooks/search.svelte"
  import { useDialog } from "$lib/hooks/use-dialog.svelte"
  import ArrowLeftIcon from "@lucide/svelte/icons/arrow-left"
  import EllipsisVerticalIcon from "@lucide/svelte/icons/ellipsis-vertical"
  import FilterIcon from "@lucide/svelte/icons/filter"
  import LoaderCircleIcon from "@lucide/svelte/icons/loader-circle"
  import PlusIcon from "@lucide/svelte/icons/plus"
  import RefreshCwIcon from "@lucide/svelte/icons/refresh-cw"
  import { createInfiniteQuery, createQuery } from "@tanstack/svelte-query"
  import DialogDetail from "./_components/dialog-detail.svelte"
  import { CustomerNooEmpty, type CustomerNoo } from "./_types"


  const prefix = $derived(page.params.prefix ?? "0")

  const filter = createFilter<{
    category: Relation
  }>({
    category: RelationEmpty
  })

  let bottomOfPage = $state<HTMLDivElement>()

  let selected = $state<CustomerNoo>(CustomerNooEmpty)
  const detailDialog = useDialog()

  const categories = createQuery(() => ({
    queryKey: ["customer-noo-category", prefix],
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const response = await httpClient.get<_response<Relation[]>>(`/api/${prefix}/customer-noos/master-category`)
      return response.data.result ?? []
    }
  }))

  const search = createSearch()
  const query = createInfiniteQuery(() => ({
    queryKey: ["customer-noo", {
      prefix,
      category_id: filter.state.category.id,
      search: search.debounced
    }],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {

      const response = await httpClient.get<_response<CustomerNoo[]>>(`/api/${prefix}/customer-noos`, {
        params: {
          page: pageParam,
          category_id: filter.state.category.id,
          search: search.debounced
        }
      })

      return response.data.result ?? []
    },
    getNextPageParam: (last, pages) => (last.length === 0 ? undefined : pages.length),
  }))

  const rows = $derived(query.data?.pages.flat() ?? [])

  function handleDetail(customer: CustomerNoo) {
    selected = customer
    detailDialog.setOpen(true)
  }

  function handleCategory(category: Relation) {
    filter.set("category", category)
    document.keyEsc()
  }

  $effect(() => {
    if (!bottomOfPage) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && query.hasNextPage && !query.isFetching) {
          void query.fetchNextPage()
        }
      },
      { threshold: 0.5 },
    )

    observer.observe(bottomOfPage)

    return () => observer.disconnect()
  })

</script>

<main class="overflow-auto relative mx-auto h-full max-w-3xl">
  <nav class="sticky w-full top-0 inset-s-0 inset-e-0 z-10 bg-background border-b max-w-3xl">
    <div class="flex h-12 items-center justify-between px-3">
      <div class="flex items-center">
        <Button
          href={`/${prefix}/home`}
          variant="ghost"
          size="icon"
          class="me-2 rounded-full"
          aria-label="Kembali">
          <ArrowLeftIcon aria-hidden="true"/>
        </Button>
        <h1 class="text-lg font-medium">Noo</h1>
      </div>
      <div>
        <Button
          href={`/${prefix}/customer-noo/create`}
          variant="ghost"
          size="icon"
          class="rounded-full"
          aria-label="Tambah Noo">
          <PlusIcon aria-hidden="true"/>
        </Button>
      </div>
    </div>
    <div class="flex gap-2 px-3 py-2">
      <Button
        variant="outline"
        size="icon"
        class="group"
        aria-label="Muat ulang"
        data-loading={query.isFetching}
        disabled={query.isFetching}
        onclick={() => query.refetch()}>
        <LoaderCircleIcon
          class="invisible absolute animate-spin group-data-[loading=true]:visible"
          aria-hidden="true"/>
        <RefreshCwIcon
          class="group-data-[loading=true]:invisible"
          aria-hidden="true"/>
      </Button>
      <Input
        type="text"
        autocapitalize="off"
        placeholder="Cari..."
        value={search.state}
        oninput={(event) => search.set(event.currentTarget.value)}/>
      <div class="flex-1"></div>
      <Popover>
        <PopoverTrigger>
          {#snippet child({ props })}
            <Button
              {...props}
              variant="outline"
              size="icon"
              aria-label="Filter">
              <FilterIcon aria-hidden="true"/>
            </Button>
          {/snippet}
        </PopoverTrigger>
        <PopoverContent
          align="end"
          class="relative grid w-72 gap-4 p-4"
          onOpenAutoFocus={(event) => event.preventDefault()}>
          <div class="space-y-1">
            <Label>Kategori</Label>
            <Popover>
              <PopoverButton
                class="w-full min-w-0"
                value={filter.state.category.name || "Semua Kategori"}/>
              <PopoverContent>
                <Command>
                  <CommandInput placeholder="Cari..."/>
                  <CommandList class="min-h-20">
                    <CommandEmpty>Tidak ada data</CommandEmpty>
                    <CommandGroup>
                      {#each categories.data ?? [] as category (category.id)}
                        <CommandItem
                          value={category.name}
                          onclick={() => handleCategory(category)}>
                          {category.name}
                        </CommandItem>
                      {/each}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  </nav>
  <div class="min-h-full">
    {#if !query.isFetching && !query.isError && rows.isEmpty()}
      <div class="px-3 py-4 text-center text-xs">Tidak ada data</div>
    {:else if !query.isFetching && query.isError}
      <div class="flex flex-col items-center gap-4 px-3 py-8 text-center text-sm">
        <div>Terjadi kesalahan saat memuat data.</div>
        <Button onclick={() => query.refetch()}>Muat Ulang</Button>
      </div>
    {/if}
    {#each rows as customer (customer.id)}
      <div class="flex items-center gap-3 px-3 py-2 text-sm hover:bg-muted">
        <div class="flex-1">
          <div class="flex items-center gap-1 text-xs">
            <div class="tabular-nums tracking-tight">
              {customer.datetime.toDateFormat("dd/MM/yyyy")}
            </div>
            <div class="text-[0.5rem] text-muted-foreground">|</div>
            <div class="tabular-nums tracking-tight">{customer.number}</div>
            <div class="flex-1"></div>
            <div>{customer.approval.name}</div>
          </div>
          <div>{customer.name}</div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger>
            {#snippet child({ props })}
              <Button
                {...props}
                variant="ghost"
                size="icon-xs"
                aria-label={`Menu ${customer.name}`}>
                <EllipsisVerticalIcon aria-hidden="true"/>
              </Button>
            {/snippet}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onclick={() => handleDetail(customer)}>Detail</DropdownMenuItem>
            <DropdownMenuItem
              disabled={customer.approval.id == 1}
              onclick={() => void goto(`/${prefix}/customer-noo/${customer.id}`)}>
              Ubah
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    {/each}
  </div>
  <div
    bind:this={bottomOfPage}
    class="h-1">
  </div>
</main>
<DialogDetail
  open={detailDialog.state}
  setOpen={detailDialog.setOpen}
  select={selected}/>
